import React, { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTrafficVisitMutation } from '@services/api';
import { readTrafficEntry } from '@utils/trafficLink';

/** Consume the entry before mounting pages that may redirect or use its query parameters. */
export const TrafficVisitBoundary: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [reportVisit] = useTrafficVisitMutation();
  const consumedEntry = useRef<string | null>(null);
  const entry = readTrafficEntry(location.search);

  useEffect(() => {
    if (!entry.hasParameter) return;
    const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    const entryPath = `${location.pathname}${location.search}${location.hash}`;
    const browserKey = window.history.state?.key ?? 'default';
    if (currentPath !== entryPath || browserKey !== location.key) return;
    const entryKey = `${location.key}:${entryPath}`;
    if (consumedEntry.current === entryKey) return;
    consumedEntry.current = entryKey;

    try {
      if (entry.mark) {
        void reportVisit({ mark: entry.mark }).unwrap().catch(() => {});
      }
    } catch {
      // A failed report must never block entry into the site. There is no automatic retry.
    } finally {
      navigate({ pathname: location.pathname, search: entry.cleanSearch, hash: location.hash }, {
        replace: true,
        state: location.state,
        preventScrollReset: true,
      });
    }
    // No cleanup abort: the pending report belongs to the consumed entry, even after navigation.
  }, [entry.hasParameter, entry.mark, entry.cleanSearch, location, navigate, reportVisit]);

  return entry.hasParameter ? null : <>{children}</>;
};
